import { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { toast } from 'sonner';
import {
  Crown,
  Star,
  Heart,
  Users,
  DollarSign,
  TrendingUp,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  ArrowLeft,
  Check,
  AlertCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';

function MetricCard({ icon: Icon, label, value, subtext, color = 'secondary' }) {
  return (
    <Card className="p-4 bg-card/50 border border-border" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg ${color === 'green' ? 'bg-green-500/20' : color === 'amber' ? 'bg-amber-500/20' : 'bg-secondary/20'}`}>
          <Icon className={`w-4 h-4 ${color === 'green' ? 'text-green-400' : color === 'amber' ? 'text-amber-400' : 'text-secondary'}`} />
        </div>
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
    </Card>
  );
}

function TierCard({ tier, onEdit, onDelete, isDefault }) {
  const tierIcons = { free: Heart, supporter: Star, vip: Crown };
  const Icon = tierIcons[tier.tier_id] || Star;

  return (
    <Card className="p-4 bg-card/50 border border-border" data-testid={`tier-manage-${tier.tier_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-secondary/20">
            <Icon className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              {tier.name}
              {isDefault && <Badge variant="outline" className="text-xs">Default</Badge>}
              {!tier.is_active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
            </h3>
            <p className="text-sm text-muted-foreground">{tier.description}</p>
          </div>
        </div>
        <div className="text-right">
          {tier.price_cents === 0 ? (
            <span className="text-lg font-bold text-green-400">Free</span>
          ) : (
            <span className="text-lg font-bold">${(tier.price_cents / 100).toFixed(2)}<span className="text-sm text-muted-foreground">/mo</span></span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
        <Users className="w-4 h-4" />
        <span>{tier.subscriber_count || 0} subscribers</span>
      </div>

      <ul className="space-y-1 mb-4">
        {(tier.benefits || []).map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" />
            <span className="text-muted-foreground">{b}</span>
          </li>
        ))}
      </ul>

      {!isDefault && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="border-border" onClick={() => onEdit(tier)} data-testid={`edit-tier-${tier.tier_id}`}>
            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
          <Button variant="outline" size="sm" className="border-red-500/20 text-red-400 hover:bg-red-500/10" onClick={() => onDelete(tier)} data-testid={`delete-tier-${tier.tier_id}`}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
          </Button>
        </div>
      )}
    </Card>
  );
}

function SubscriberRow({ sub }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center overflow-hidden">
          {sub.subscriber?.avatar_url ? (
            <img src={sub.subscriber.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <Users className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium">{sub.subscriber?.name || 'Fan'}</p>
          <p className="text-xs text-muted-foreground">{sub.tier_id}</p>
        </div>
      </div>
      <div className="text-right">
        <Badge className={sub.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
          {sub.status}
        </Badge>
        <p className="text-xs text-muted-foreground mt-1">
          {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ''}
        </p>
      </div>
    </div>
  );
}

export default function CreatorSubscriptionsPage() {
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState('overview');
  const [tiers, setTiers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [artistId, setArtistId] = useState(null);

  // Tier editor state
  const [editDialog, setEditDialog] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm, setTierForm] = useState({ name: '', price_cents: 499, description: '', benefits: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Get current user
      const meRes = await fetch(`${API}/auth/me`, { headers: getAuthHeaders() });
      if (!meRes.ok) return;
      const meData = await safeJson(meRes);
      const me = meData.data || meData;

      // Look up artist profile by user_id
      const artistRes = await fetch(`${API}/artists`, { headers: getAuthHeaders() });
      let aid = null;
      if (artistRes.ok) {
        const artistsData = await artistRes.json();
        const artists = Array.isArray(artistsData) ? artistsData : (artistsData.artists || []);
        const myArtist = artists.find(a => a.user_id === me.user_id);
        if (myArtist) aid = myArtist.artist_id;
      }

      if (!aid) { setLoading(false); return; }
      setArtistId(aid);

      const [tiersRes, subsRes, earningsRes] = await Promise.all([
        fetch(`${API}/fan-subscriptions/tiers/${aid}`, { headers: getAuthHeaders() }),
        fetch(`${API}/fan-subscriptions/artist/subscribers`, { headers: getAuthHeaders() }),
        fetch(`${API}/fan-subscriptions/artist/earnings`, { headers: getAuthHeaders() }),
      ]);

      if (tiersRes.ok) {
        const d = await safeJson(tiersRes);
        setTiers((d.data || d).tiers || []);
      }
      if (subsRes.ok) {
        const d = await safeJson(subsRes);
        setSubscribers((d.data || d).subscribers || []);
      }
      if (earningsRes.ok) {
        const d = await safeJson(earningsRes);
        setEarnings(d.data || d);
      }
    } catch (e) {
      console.error('Failed to load subscription data:', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEditTier = (tier) => {
    setEditingTier(tier);
    setTierForm({
      name: tier.name,
      price_cents: tier.price_cents,
      description: tier.description || '',
      benefits: (tier.benefits || []).join('\n'),
    });
    setEditDialog(true);
  };

  const handleCreateTier = () => {
    setEditingTier(null);
    setTierForm({ name: '', price_cents: 499, description: '', benefits: '' });
    setEditDialog(true);
  };

  const handleSaveTier = async () => {
    if (!tierForm.name.trim()) { toast.error('Name is required'); return; }
    if (tierForm.price_cents < 199 || tierForm.price_cents > 9999) {
      toast.error('Price must be between $1.99 and $99.99');
      return;
    }

    setSaving(true);
    try {
      const body = {
        name: tierForm.name.trim(),
        price_cents: parseInt(tierForm.price_cents),
        description: tierForm.description.trim(),
        benefits: tierForm.benefits.split('\n').map(b => b.trim()).filter(Boolean),
      };

      let res;
      if (editingTier && editingTier.tier_id && !editingTier.is_default) {
        res = await fetch(`${API}/fan-subscriptions/tiers/${editingTier.tier_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API}/fan-subscriptions/tiers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(body),
        });
      }

      if (res.ok) {
        toast.success(editingTier ? 'Tier updated' : 'Tier created');
        setEditDialog(false);
        fetchData();
      } else {
        const err = await safeJson(res);
        toast.error(err.data?.detail || 'Failed to save tier');
      }
    } catch {
      toast.error('Failed to save tier');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTier = async (tier) => {
    if (!window.confirm(`Delete "${tier.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/fan-subscriptions/tiers/${tier.tier_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        toast.success('Tier deleted');
        fetchData();
      } else {
        const err = await safeJson(res);
        toast.error(err.data?.detail || 'Failed to delete tier');
      }
    } catch {
      toast.error('Failed to delete tier');
    }
  };

  const metrics = earnings?.metrics || {};
  const tabs = ['overview', 'tiers', 'subscribers'];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (!artistId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Artist Profile Required</h2>
          <p className="text-muted-foreground mb-4">You need an artist profile to manage subscriptions.</p>
          <Link to="/settings">
            <Button>Go to Settings</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="creator-subscriptions-page">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/settings">
            <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Subscription Manager</h1>
            <p className="text-sm text-muted-foreground">Manage tiers, subscribers & earnings</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border pb-2 overflow-x-auto">
          {tabs.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                tab === t ? 'text-white bg-muted/50' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${t}`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard icon={Users} label="Subscribers" value={metrics.active_subscribers || 0} color="secondary" />
              <MetricCard icon={DollarSign} label="Monthly Revenue" value={metrics.monthly_recurring_revenue_formatted || '$0.00'} color="green" />
              <MetricCard icon={TrendingUp} label="Lifetime Earned" value={metrics.lifetime_earnings_formatted || '$0.00'} color="amber" />
              <MetricCard icon={DollarSign} label="Pending" value={metrics.pending_balance_formatted || '$0.00'} subtext="Available after payout setup" />
            </div>

            {/* Quick actions */}
            <Card className="p-4 bg-card/50 border border-border">
              <h3 className="font-semibold mb-3">Quick Actions</h3>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setTab('tiers')} data-testid="manage-tiers-btn">
                  <Crown className="w-4 h-4 mr-1" /> Manage Tiers
                </Button>
                <Button variant="outline" size="sm" onClick={() => setTab('subscribers')} data-testid="view-subscribers-btn">
                  <Users className="w-4 h-4 mr-1" /> View Subscribers
                </Button>
                <Link to="/content-locks">
                  <Button variant="outline" size="sm">
                    Content Locks
                  </Button>
                </Link>
              </div>
            </Card>

            {/* Fees info */}
            <Card className="p-4 bg-card/50 border border-border">
              <h3 className="font-semibold mb-2">Fee Structure</h3>
              <div className="flex items-center gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Creator share:</span>
                  <span className="ml-1 font-bold text-green-400">{earnings?.fees?.creator_share_percent || 85}%</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Platform fee:</span>
                  <span className="ml-1">{earnings?.fees?.platform_fee_percent || 15}%</span>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Tiers Tab */}
        {tab === 'tiers' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Subscription Tiers</h2>
              <Button size="sm" onClick={handleCreateTier} data-testid="create-tier-btn">
                <Plus className="w-4 h-4 mr-1" /> New Tier
              </Button>
            </div>

            {tiers.map(tier => (
              <TierCard
                key={tier.tier_id}
                tier={tier}
                isDefault={tier.is_default}
                onEdit={handleEditTier}
                onDelete={handleDeleteTier}
              />
            ))}

            {tiers.length === 0 && (
              <Card className="p-8 text-center bg-card/50 border border-border">
                <Crown className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-3">No custom tiers yet. Default tiers are shown to fans.</p>
                <Button onClick={handleCreateTier}><Plus className="w-4 h-4 mr-1" /> Create Tier</Button>
              </Card>
            )}
          </div>
        )}

        {/* Subscribers Tab */}
        {tab === 'subscribers' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Active Subscribers ({subscribers.length})</h2>

            {subscribers.length > 0 ? (
              <Card className="bg-card/50 border border-border p-4">
                {subscribers.map((sub, i) => (
                  <SubscriberRow key={sub.subscription_id || i} sub={sub} />
                ))}
              </Card>
            ) : (
              <Card className="p-8 text-center bg-card/50 border border-border">
                <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No subscribers yet. Share your profile to get started!</p>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Tier Editor Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>{editingTier ? 'Edit Tier' : 'Create Tier'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Name</label>
              <Input
                value={tierForm.name}
                onChange={e => setTierForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Supporter, VIP, Premium"
                data-testid="tier-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Price (cents)</label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={199}
                  max={9999}
                  value={tierForm.price_cents}
                  onChange={e => setTierForm(p => ({ ...p, price_cents: parseInt(e.target.value) || 0 }))}
                  data-testid="tier-price-input"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  = ${(tierForm.price_cents / 100).toFixed(2)}/mo
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Input
                value={tierForm.description}
                onChange={e => setTierForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description of this tier"
                data-testid="tier-desc-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Benefits (one per line)</label>
              <Textarea
                rows={4}
                value={tierForm.benefits}
                onChange={e => setTierForm(p => ({ ...p, benefits: e.target.value }))}
                placeholder="Exclusive content access&#10;Early releases&#10;Direct messages"
                data-testid="tier-benefits-input"
              />
            </div>
            <Button className="w-full" onClick={handleSaveTier} disabled={saving} data-testid="save-tier-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {editingTier ? 'Update Tier' : 'Create Tier'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
